import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import { styled } from "@mui/system";
import { useEffect, useState } from "react";
import Markdown from "react-markdown";
import { EmRender, PRender, UlRender } from "../MarkdownRenders";
import { Close } from "@mui/icons-material";
import { getUIVersion } from "../../utils/version";

const MarkdownContainer = styled(Box)(() => ({
  padding: "16px 0",
  overflow: "auto",
  maxHeight: "80vh",
}));

interface Props {
  open: boolean;
  handleClose: (hasFinished?: boolean) => void;
}

const ChangelogDialog: React.FC<Props> = ({ open, handleClose }) => {
  const [changelogText, setChangelogText] = useState("");

  useEffect(() => {
    // Fetch version info which includes release notes
    getUIVersion()
      .then((versionInfo) => {
        if (versionInfo?.releaseNotes) {
          // Format release date from buildDate (format: 2025-01-15T14:30:00Z -> 2025-01-15)
          const releaseDate = versionInfo.buildDate 
            ? new Date(versionInfo.buildDate).toISOString().split('T')[0]
            : new Date().toISOString().split('T')[0];
          
          // Build markdown from release notes
          const notes = versionInfo.releaseNotes;
          const releaseType = notes.type ?? "Release";
          const items = notes.items ?? [];
          
          // Format as markdown
          const formattedItems = items.map(item => `* ${item}`).join('\n');
          const markdown = `### Version ${versionInfo.version}\n*${releaseDate}*\n\n**${releaseType}**\n${formattedItems}`;
          
          setChangelogText(markdown);
        } else {
          // Fallback to changelog.txt if no release notes in version.json
          fetch("/changelog.txt")
            .then((res) => res.text())
            .then((text) => {
              // Replace version placeholder with actual version
              const updatedText = text.replace(
                /###?\s*Version\s+X\.Y\.Z/gi,
                `### Version ${versionInfo.version}`
              );
              // Replace date placeholder if exists
              const finalText = updatedText.replace(
                /\*\d{4}-\d{2}-\d{2}\*/g,
                `*${versionInfo.buildDate ? new Date(versionInfo.buildDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0]}*`
              );
              setChangelogText(finalText);
            })
            .catch(() => {
              setChangelogText(`### Version ${versionInfo.version}\n\nVersion information available, but release notes not found.`);
            });
        }
      })
      .catch(() => {
        // Final fallback
        fetch("/changelog.txt")
          .then((res) => res.text())
          .then(setChangelogText)
          .catch(() => {
            setChangelogText("### Version information unavailable");
          });
      });
  }, []);

  return (
    <Dialog
      open={open}
      onClose={() => handleClose()}
      scroll={"paper"}
      sx={{
        "& .MuiPaper-root": {
          width: "606px",
          minHeight: "367px",
          padding: "32px 24px",
        },
      }}
      aria-labelledby="changelog-dialog-title"
      aria-describedby="changelog-dialog-description"
    >
      <DialogTitle component="div" sx={{ padding: "0" }}>
        <Typography
          id="changelog-dialog-title"
          variant="h4"
          className="red-bar"
          pt="12px"
        >
          Release notes
        </Typography>
        <IconButton onClick={() => handleClose()} sx={{ position: "absolute", top: 8, right: 8 }}>
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ padding: 0 }}>
        <MarkdownContainer>
          <Markdown
            components={{
              ul: UlRender,
              em: EmRender,
              p: PRender,
            }}
          >
            {changelogText}
          </Markdown>
        </MarkdownContainer>
      </DialogContent>
    </Dialog>
  );
};

export default ChangelogDialog;
